import PropTypes from "prop-types";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { loadBenefitPlans, loadViewClaim } from "actions/claimActions";
import { getBenefitPlan, getClaim, getFieldsByPlanIdAndItemType } from "reducers/claimsReducer";
import { FilePreview, Button, Toolbar, ToolbarSection, Loader } from "cxa-components";
import { Modal } from "@cxagroup/casper";
import { PrintIcon } from "cxa-icons";
import { filterNull, getFileProps } from "cxa-utils";
import FormHeading from "components/common/FormHeading";
import RenderField from "cxa-components/field";
import { FormattedCustomDate } from "components/common/FormattedComponents";
import { intlShape, injectIntl, FormattedHTMLMessage, FormattedMessage } from "react-intl";
import messages from "components/messagesDescription";
import { SystemMessageActions as SystemMessage } from "actions";
import { renderAsHTML } from "../../utils";
import StandardMoney from "../common/StandardMoney";
import Select from "react-select";
import { getDelegatedEmployees, postExpenseClaim } from "../../actions/claimActionsPP";

const renderFiles = value => {
  const files = (value && [].concat(value).filter(filterNull)) || [];
  return (
    <div>
      {files.length > 0
        ? files.map(file => {
            const props = getFileProps(file);
            return <FilePreview key={props.name} className="u-margin-top" {...props} />;
          })
        : "-"}
    </div>
  );
};

renderFiles.propTypes = {
  name: PropTypes.string,
};

const formatValue = (value, type, item, field, intl) => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return "-";
  }

  switch (type) {
    case "Date":
      return <FormattedCustomDate value={value} />;
    case "List":
    case "Autocomplete": {
      if (field.name === "provider" && item.postalCode) {
        return renderAsHTML("span", `${item.providerName}, ${item.postalCode}`);
      }
      const res = field.items.find(entry => entry.value === value);
      return renderAsHTML(
        "span",
        (res && intl.formatMessage({ id: res.label, defaultMessage: res.label })) || value
      );
    }
    case "Decimal":
      if (field.name === "foreignReceiptAmount" || !item.displayCurrency) {
        return <StandardMoney value={value} />;
      }
      return <StandardMoney value={value} currency={item.currencyType} />;
    case "Receipt":
    case "Referral":
      return renderFiles(value);
    case "TextArea":
    case "String":
      return renderAsHTML("span", intl.formatMessage({ id: value, defaultMessage: value }));
    case "Boolean":
      return value
        ? intl.formatMessage(messages.yesTextDescription)
        : intl.formatMessage(messages.noTextDescription);
    default:
      return value;
  }
};

const renderOutput = ({ id, value }) => <div id={`${id}`}>{value}</div>;

renderOutput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.any,
};

const renderOutputPreLine = ({ id, value }) => (
  <div id={id} style={{ "white-space": "pre-line" }}>
    {value}
  </div>
);

renderOutputPreLine.propTypes = {
  id: PropTypes.number,
  value: PropTypes.string,
};

const renderReimbursements = (field, claim, intl) => {
  const { reimbursementDetailList } = claim;
  const fieldType = field.dataType;

  const value = formatValue(claim[field.name], fieldType, claim, field, intl);
  return (
    <div>
      <RenderField
        id={field.name}
        key={field.name}
        label={intl.formatMessage({
          id: field.label,
          defaultMessage: field.label,
        })}
        value={value}
        field={renderOutput}
      />
      <table className="table--minimal reimbursement-table">
        <tbody>
          <tr>
            <th>
              <u>{intl.formatMessage(messages.claimAccountsTextDescription)}</u>
            </th>
            <th>
              <u>{intl.formatMessage(messages.claimReimbursementTextDescription)}</u>
            </th>
          </tr>
          {reimbursementDetailList &&
            reimbursementDetailList.map(item => {
              return (
                <tr key={item.accountName}>
                  <td>
                    {intl.formatMessage({
                      id: item.accountName,
                      defaultMessage: item.accountName,
                    })}
                  </td>
                  <td>
                    {item.displayCurrency ? (
                      <StandardMoney value={item.amount} currency={claim.currencyType} />
                    ) : (
                      <StandardMoney value={item.amount} />
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

renderReimbursements.propTypes = {
  field: PropTypes.object,
  claim: PropTypes.object,
  intl: PropTypes.object,
};

const ClaimInstructionsModal = ({ instructions, isOpen, close, claimId, formLabel }) => (
  <Modal isOpen={isOpen} onRequestClose={close} contentLabel="Claim Instructions">
    <div className="modal__body">
      <p>
        <FormattedHTMLMessage
          {...messages.formSubmittedSuccessfullyDescription}
          values={{ formLabel, claimId }}
        />
      </p>
      {instructions && <p dangerouslySetInnerHTML={{ __html: instructions }} />}
      <hr />
    </div>
    <div className="modal__footer">
      <Button type="button" secondary onClick={close}>
        <FormattedMessage {...messages.okTextDescription} />
      </Button>
    </div>
  </Modal>
);

ClaimInstructionsModal.propTypes = {
  instructions: PropTypes.string,
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  claimId: PropTypes.string,
  formLabel: PropTypes.string,
};

const ClaimDetails = ({ claim, benefitPlan, fields, getDonateCategory, intl }) => {
  // why this was originally an array, I'd never know nor do I want to find out
  const pageTitle = (
    <span>
      {getDonateCategory
        ? intl.formatMessage(messages.donationIDDescription)
        : intl.formatMessage({
            id: fields.documentId.label,
            defaultMessage: fields.documentId.label,
          })}
      <span id="claimId"> {claim.id}</span>
    </span>
  );
  const claimItemLabel = fields.claimItemType.label;
  const planName = benefitPlan ? benefitPlan.planName : null;
  const claimName = claim ? claim.claimItemName : null;
  const taxStatus = claim.taxStatus
    ? intl.formatMessage(messages.taxableDescription)
    : intl.formatMessage(messages.nonTaxableDescription);
  const taxStatusLabel = fields.taxStatus.label;

  const infoFields = [
    {
      label: "Status",
      value: claim.status,
      id: "claimStatus",
    },
    {
      label: intl.formatMessage(messages.benefitPlanDescription),
      value: planName,
      id: "planName",
    },
    {
      label: claimItemLabel,
      value: claimName,
      id: "claimName",
    },
    {
      label: taxStatusLabel,
      value: taxStatus,
      id: "taxStatus",
    },
  ];

  if (claim.isCPFEnabled) {
    const cpfContributable = claim.cpfContributable
      ? intl.formatMessage(messages.yesTextDescription)
      : intl.formatMessage(messages.noTextDescription);
    const cpfStatusLabel =
      fields && fields.cPFContributable
        ? intl.formatMessage({
            id: fields.cPFContributable.label,
            defaultMessage: fields.cPFContributable.label,
          })
        : null;

    const cpfContributableObject = {
      label: cpfStatusLabel,
      value: cpfContributable,
      id: "cpfContributable",
    };

    infoFields.push(cpfContributableObject);
  }

  const claimTypeIdArr = claim.claimTypeId && claim.claimTypeId.split("-");
  const isInsuranceClaim =
    claimTypeIdArr && (claimTypeIdArr[0] === "HS" || claimTypeIdArr[0] === "MO");

  let fieldsArray = (fields && fields.fields) || [];

  if (claim) {
    let tempArr = [];
    for (const field of fieldsArray) {
      if (claim.estimatedPaymentDate && !isInsuranceClaim && field.name === "remarks") {
        tempArr.push({
          dataType: "Date",
          defaultAsBlank: true,
          disabled: false,
          label: "Estimated Payment Date",
          name: "estimatedPaymentDate",
          readOnly: true,
          required: false,
          _dataType: "Date",
        });
      } else if (
        field.name === "bankCode" ||
        field.name === "branchCode" ||
        field.name === "bankAccountNo"
      ) {
        infoFields.push({ label: field.label, value: field.value, id: field.name });
        continue;
      }
      tempArr.push(field);
    }
    fieldsArray = tempArr;
  }

  return (
    <div>
      <FormHeading title={pageTitle} fields={infoFields} />
      <div>
        {fieldsArray &&
          fieldsArray.map(field => {
            const fieldType = field.dataType;
            const value = formatValue(
              claim.lowercasedKeys[field.fieldLowercased || field.name],
              fieldType,
              claim,
              field,
              intl
            );
            if (field.name === "paymentAmount") {
              return renderReimbursements(field, claim, intl);
            } else if (
              claim.hideFlexClaimPaymentDate &&
              field.name === "paymentDate" &&
              !isInsuranceClaim
            ) {
              return null;
            } else {
              return (
                <div>
                  <RenderField
                    id={field.name}
                    key={field.name}
                    label={intl.formatMessage({
                      id: field.label,
                      defaultMessage: field.label,
                    })}
                    value={intl.formatMessage({
                      id: value,
                      defaultMessage: value,
                    })}
                    field={renderOutput}
                  />
                </div>
              );
            }
          })}
      </div>
    </div>
  );
};

ClaimDetails.propTypes = {
  benefitPlan: PropTypes.object,
  claim: PropTypes.object,
  fields: PropTypes.object,
  intl: PropTypes.object,
};

class ViewClaimPage extends React.Component {
  constructor(props) {
    super(props);
    this.closeClaimInstructionsModal = this.closeClaimInstructionsModal.bind(this);
  }

  state = {
    isFetching: true,
    selectedDecision: null,
    rejectReason: "",
    decisionOptions: [
      { value: "approved", label: this.props.intl.formatMessage(messages.claims.approve) },
      { value: "rejected", label: this.props.intl.formatMessage(messages.claims.reject) },
    ],
    setModalIsOpen: false,
  };

  componentDidMount() {
    const {
      loadBenefitPlans,
      loadViewClaim,
      claimId,
      selectedDelegator,
      getDonateCategory,
      // eslint-disable-next-line react/prop-types
      getDelegatedEmployees,
    } = this.props;

    loadBenefitPlans();
    getDelegatedEmployees();
    loadViewClaim(claimId, selectedDelegator, getDonateCategory);
    //Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    const { history } = this.props;
    const nxtMsg = nextProps.systemMessage;

    if (
      nxtMsg.data &&
      nxtMsg.data.module === "claims" &&
      nxtMsg.msgType === SystemMessage.MessageTypes.CRITICAL
    ) {
      history.push("/404");
    }

    if (!nextProps.isFetching) {
      this.setState({
        isFetching: false,
      });
    }
  }

  closeClaimInstructionsModal(claimId, selectedDelegator, getDonateCategory) {
    const path = selectedDelegator
      ? `/${getDonateCategory ? "donations" : "claims"}/view/${claimId}/${selectedDelegator}`
      : `/${getDonateCategory ? "donations" : "claims"}/view/${claimId}`;

    this.props.push(path);
  }

  handleDecisionChange = selectedOption => {
    this.setState({ selectedDecision: selectedOption.value || null });
  };

  handleRejectReasonChange = event => {
    this.setState({ rejectReason: event.target.value });
  };

  handleSubmitDecision = () => {
    const { selectedDecision, rejectReason } = this.state;
    // eslint-disable-next-line react/prop-types
    const { postExpenseClaim, claimId, selectedDelegator, history } = this.props;

    const requestData = selectedDecision === "rejected" ? { reason: rejectReason } : {};

    postExpenseClaim(selectedDecision, claimId, selectedDelegator, requestData, history);
  };

  handleCloseModal = () => {
    this.setState({ setModalIsOpen: false });
  };

  handleOpenModal = () => {
    this.setState({ setModalIsOpen: true });
  };

  render() {
    if (this.state.isFetching) {
      return <Loader />;
    }

    const {
      claim,
      claimId,
      status,
      claimInstruction,
      intl,
      selectedDelegator,
      getDonateCategory,
      delegatedEmployees,
    } = this.props;
    const { editable } = claim;

    const { selectedDecision, decisionOptions } = this.state;

    const selectedEmployee = delegatedEmployees.find(
      emp => String(emp.employeeKey) === String(selectedDelegator)
    );

    const isManager =
      selectedEmployee !== undefined &&
      selectedEmployee !== null &&
      selectedEmployee.role === "Manager";

    // modal disclaimer
    const MessageModal = (
      <Modal isOpen={this.state.setModalIsOpen} onRequestClose={close}>
        <div className="modal__body">
          <span>
            {selectedDecision === "approved"
              ? intl.formatMessage(messages.claims.approveDescription)
              : intl.formatMessage(messages.claims.rejectDescription)}
          </span>
        </div>
        <div className="modal__footer" style={{ textAlign: "right", marginTop: "10px" }}>
          <Button primary type="button" className="download-button" onClick={this.handleCloseModal}>
            {intl.formatMessage(messages.cancelTextDescription)}
          </Button>
          <Button
            primary
            type="button"
            className="download-button"
            onClick={() => {
              this.handleSubmitDecision();
            }}
          >
            {intl.formatMessage(messages.okTextDescription)}
          </Button>
        </div>
      </Modal>
    );

    return (
      <div>
        {MessageModal}
        <ClaimInstructionsModal
          instructions={intl.formatMessage({ id:claimInstruction, defaultMessage: claimInstruction})}
          isOpen={status === "new"}
          close={() =>
            this.closeClaimInstructionsModal(claimId, selectedDelegator, getDonateCategory)
          }
          claimId={claim.id}
          formLabel={intl.formatMessage({
            id: this.props.fields.documentId.label,
            defaultMessage: this.props.fields.documentId.label,
          })}
        />
        <Toolbar>
          <ToolbarSection />
          <ToolbarSection>
            <Button primary className="apl-no-print" onClick={window.print}>
              <PrintIcon />
              {intl.formatMessage(messages.printTextDescription)}
            </Button>
          </ToolbarSection>
        </Toolbar>
        <div className="o-layout">
          <div className="o-layout__item">
            <article className="card">
              <section>
                <ClaimDetails {...this.props} />

                {isManager && (
                  <div style={{ maxWidth: "30%" }}>
                    <div style={{ marginBottom: "15px" }}>
                      <p style={{ marginBottom: "5px" }}>
                        <strong>{intl.formatMessage(messages.claims.claimDecision)}</strong>
                      </p>
                      <Select
                        options={decisionOptions}
                        isClearable={false}
                        value={decisionOptions.find(option => option.value === selectedDecision)}
                        onChange={this.handleDecisionChange}
                      ></Select>
                    </div>
                    {selectedDecision === "rejected" && (
                      <div>
                        <p style={{ marginBottom: "5px" }}>
                          <strong>{intl.formatMessage(messages.claims.rejectReason)}</strong>
                        </p>
                        <textarea
                          rows={3}
                          className="textfield--boxed"
                          style={{
                            width: "100%",
                            borderRadius: "3px",
                            fontFamily: "Poppins Regular",
                          }}
                          onChange={this.handleRejectReasonChange}
                        ></textarea>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </article>
            <p>
              {isManager ? (
                <Button
                  className="apl-no-print"
                  onClick={this.handleOpenModal}
                  disabled={
                    !selectedDecision ||
                    (selectedDecision === "rejected" && this.state.rejectReason === "")
                  }
                >
                  {intl.formatMessage(messages.submitTextDescription)}
                </Button>
              ) : (
                <Fragment>
                  {editable && (
                    <Button
                      className="apl-no-print"
                      to={
                        selectedDelegator
                          ? `/${
                              getDonateCategory ? "donations" : "claims"
                            }/edit/${claimId}/${selectedDelegator}`
                          : `/${getDonateCategory ? "donations" : "claims"}/edit/${claimId}`
                      }
                    >
                      {intl.formatMessage(messages.editDescription)}
                    </Button>
                  )}
                </Fragment>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }
}

ViewClaimPage.propTypes = {
  claimId: PropTypes.string,
  claim: PropTypes.object,
  fields: PropTypes.object,
  loadBenefitPlans: PropTypes.func,
  loadViewClaim: PropTypes.func,
  loadClaimItems: PropTypes.func,
  benefitPeriod: PropTypes.any,
  claimTypeId: PropTypes.string,
  intl: intlShape.isRequired,
  isFetching: PropTypes.bool,
  status: PropTypes.string,
  claimInstruction: PropTypes.string,
  push: PropTypes.func,
  systemMessage: PropTypes.any,
  history: PropTypes.object.isRequired,
  selectedDelegator: PropTypes.number,
  getDonateCategory: PropTypes.bool,
  delegatedEmployees: PropTypes.object,
};

const mapStateToProps = (
  { claims: { claimList, benefitPlans, fields }, systemMessage, claimsPP },
  {
    match: {
      params: { claimId, selectedDelegator },
    },
    query: { status },
  }
) => {
  const claim = getClaim(claimList, claimId) || {};

  const { claimTypeId, benefitPeriod } = claim;
  const benefitPlan = getBenefitPlan(benefitPlans, benefitPeriod);

  const _fields = getFieldsByPlanIdAndItemType(fields, benefitPeriod, claimTypeId);

  const claimInstruction = fields.claimInstruction;

  return {
    claim,
    claimId,
    status,
    claimTypeId,
    benefitPeriod,
    fields: _fields,
    claimInstruction,
    benefitPlan,
    isFetching: !_fields || claimList.isFetching || fields.isFetching || benefitPlans.isFetching,
    systemMessage,
    selectedDelegator: parseInt(selectedDelegator),
    employeeDelegated: claimsPP.employeeDelegated,
    delegatedEmployees: claimsPP.delegatedEmployees,
  };
};

const mapDispatchToProps = {
  loadBenefitPlans,
  loadViewClaim,
  getDelegatedEmployees,
  postExpenseClaim,
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ViewClaimPage)));



// WEBPACK FOOTER //
// ./components/claim/ViewClaimPage.js